import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Check, Trash2, RefreshCw, Users, Clock, Link as LinkIcon, Share2 } from 'lucide-react';
import API_BASE_URL from '../../config';

const FriendsManagement = () => {
  const [inviteTokens, setInviteTokens] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  
  // Recupera l'admin ID dal localStorage
  const adminData = JSON.parse(localStorage.getItem('adminData'));
  const adminId = adminData?.id;

  // Carica i token di invito
  const loadInviteTokens = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/invites?adminId=${adminId}`);
      const data = await response.json();
      setInviteTokens(data.tokens || []);
    } catch (error) {
      console.error('Errore nel caricamento dei token:', error);
    }
  }, [adminId]);

  // Carica la lista degli amici
  const loadFriends = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/friends?adminId=${adminId}`);
      const result = await response.json();
      console.log('📊 Friends API response:', result);
      
      // Il backend restituisce { success: true, data: [...] }
      const friendsList = result.data || result.friends || [];
      console.log('📊 Friends list:', friendsList);
      setFriends(friendsList);
    } catch (error) {
      console.error('Errore nel caricamento degli amici:', error);
      setFriends([]);
    }
  }, [adminId]);

  // Genera un nuovo token di invito
  const generateNewToken = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/invites/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adminId }),
      });
      const data = await response.json();
      
      if (response.ok) {
        await loadInviteTokens();
      } else {
        alert(data.error || 'Errore nella generazione del token');
      }
    } catch (error) {
      console.error('Errore nella generazione del token:', error);
      alert('Errore nella generazione del token');
    } finally {
      setLoading(false);
    }
  };

  // Copia il link di invito
  const copyInviteLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopiedToken(link);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  // Condividi il link di invito con Web Share API
  const shareInviteLink = async (link) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Unisciti al gruppo del Becco',
          text: 'Il Becco ti ha invitato a unirti al suo gruppo! Clicca sul link per registrarti:',
          url: link
        });
      } else {
        // Fallback: copia negli appunti
        copyInviteLink(link);
        alert('Link copiato negli appunti! Condividilo con i tuoi amici.');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Se l'utente annulla la condivisione, non mostrare errore
        console.error('Errore nella condivisione:', error);
        copyInviteLink(link);
      }
    }
  };

  // Disattiva un token
  const deactivateToken = async (tokenId) => {
    if (!window.confirm('Sei sicuro di voler disattivare questo token?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/invites/${tokenId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adminId }),
      });

      if (response.ok) {
        await loadInviteTokens();
      }
    } catch (error) {
      console.error('Errore nella disattivazione del token:', error);
    }
  };

  // Pulisci i token scaduti
  const cleanupExpiredTokens = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/invites/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adminId }),
      });
      const data = await response.json();
      
      if (response.ok) {
        await loadInviteTokens();
        alert(data.message);
      }
    } catch (error) {
      console.error('Errore nella pulizia dei token:', error);
    } finally {
      setLoading(false);
    }
  };

  // Rimuovi un amico
  const removeFriend = async (friendId) => {
    if (!window.confirm('Sei sicuro di voler rimuovere questo amico dal gruppo?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/friends/${friendId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adminId }),
      });

      if (response.ok) {
        await loadFriends();
      }
    } catch (error) {
      console.error('Errore nella rimozione dell\'amico:', error);
    }
  };

  useEffect(() => {
    if (adminId) {
      loadInviteTokens();
      loadFriends();
    }
  }, [adminId, loadInviteTokens, loadFriends]);

  // Funzione per formattare la data
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Funzione per ottenere il badge di stato
  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-green-500/20 text-green-600 dark:text-green-400', text: 'Attivo' },
      used: { color: 'bg-light-primary/20 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary', text: 'Utilizzato' },
      expired: { color: 'bg-light-surface-variant/50 dark:bg-dark-surface-variant/50 text-light-on-surface-variant dark:text-dark-on-surface-variant', text: 'Scaduto' }
    };
    const badge = badges[status] || badges.expired;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-light-surface dark:bg-dark-surface p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6
          animate-fade-in opacity-0"
          style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 sm:w-8 sm:h-8 text-light-primary dark:text-dark-primary" />
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-light-on-surface dark:text-dark-on-surface">
                Gestione Amici
              </h2>
              <p className="mt-0.5 text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
                Invita nuovi amici e gestisci il gruppo
              </p>
            </div>
          </div>
        </div>

        {/* Sezione Link di Invito */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6 mb-6 sm:mb-8 animate-fade-in opacity-0"
          style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-light-on-surface dark:text-dark-on-surface flex items-center gap-2">
              <LinkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              Link di Invito
            </h2>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={cleanupExpiredTokens}
                disabled={loading}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-light-surface-variant dark:bg-dark-surface-variant text-light-on-surface dark:text-dark-on-surface rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 transition-opacity text-sm"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Pulisci Scaduti</span>
                <span className="sm:hidden">Pulisci</span>
              </button>
              <button
                onClick={generateNewToken}
                disabled={loading}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 transition-opacity text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Genera Nuovo Link</span>
                <span className="sm:hidden">Nuovo</span>
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {inviteTokens.length === 0 ? (
              <p className="text-light-on-surface-variant dark:text-dark-on-surface-variant text-center py-8">
                Nessun link di invito generato. Clicca su "Genera Nuovo Link" per crearne uno.
              </p>
            ) : (
              inviteTokens.map((token, index) => (
                <div
                  key={token.id}
                  className="border border-light-outline dark:border-dark-outline rounded-lg p-4 hover:shadow-surface-container transition-shadow animate-fade-in opacity-0"
                  style={{ animationDelay: `${300 + index * 100}ms`, animationFillMode: 'forwards' }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(token.status)}
                        <span className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Scade: {formatDate(token.expiresAt)}
                        </span>
                      </div>
                      
                      <div className="bg-light-surface dark:bg-dark-surface p-3 rounded border border-light-outline dark:border-dark-outline mb-2">
                        <code className="text-sm text-light-on-surface dark:text-dark-on-surface break-all">
                          {token.inviteLink}
                        </code>
                      </div>

                      {token.user && (
                        <div className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
                          Utilizzato da: <span className="font-medium">
                            {token.user.firstName} {token.user.lastName} (@{token.user.username})
                          </span>
                          <span className="ml-2">il {formatDate(token.usedAt)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      {token.status === 'active' && (
                        <>
                          <button
                            onClick={() => shareInviteLink(token.inviteLink)}
                            className="p-2 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary hover:opacity-90 rounded-lg transition-opacity"
                            title="Condividi link"
                          >
                            <Share2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => copyInviteLink(token.inviteLink)}
                            className="p-2 text-light-primary dark:text-dark-primary hover:bg-light-primary/10 dark:hover:bg-dark-primary/10 rounded-lg transition-colors"
                            title="Copia link"
                          >
                            {copiedToken === token.inviteLink ? (
                              <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                            ) : (
                              <Copy className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() => deactivateToken(token.id)}
                            className="p-2 text-light-error dark:text-dark-error hover:bg-light-error/10 dark:hover:bg-dark-error/10 rounded-lg transition-colors"
                            title="Disattiva link"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Sezione Lista Amici */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6 animate-fade-in opacity-0"
          style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
          <h2 className="text-xl sm:text-2xl font-semibold text-light-on-surface dark:text-dark-on-surface mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6" />
            I Tuoi Amici ({friends.length})
          </h2>

          <div className="space-y-3">
            {friends.length === 0 ? (
              <p className="text-light-on-surface-variant dark:text-dark-on-surface-variant text-center py-8">
                Nessun amico nel tuo gruppo. Genera un link di invito per invitare nuovi amici!
              </p>
            ) : (
              friends.map((friend, index) => (
                <div
                  key={friend.id}
                  className="flex justify-between items-center p-4 border border-light-outline dark:border-dark-outline rounded-lg hover:shadow-surface-container transition-shadow animate-fade-in opacity-0"
                  style={{ animationDelay: `${500 + index * 100}ms`, animationFillMode: 'forwards' }}
                >
                  <div>
                    <h3 className="font-semibold text-light-on-surface dark:text-dark-on-surface">
                      {friend.firstName} {friend.lastName}
                    </h3>
                    <p className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">@{friend.username}</p>
                    <p className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant">{friend.email}</p>
                    <p className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant opacity-70 mt-1">
                      Iscritto il: {formatDate(friend.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={() => removeFriend(friend.id)}
                    className="p-2 text-light-error dark:text-dark-error hover:bg-light-error/10 dark:hover:bg-dark-error/10 rounded-lg transition-colors"
                    title="Rimuovi amico"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FriendsManagement;
